import { PageHeaderLayout, RouteTabWithContent, TabContent } from "@homebound/beam";
import { useMemo } from "react";
import { createPlanPackagesUrl } from "src/RouteUrls";
import { useFeatureFlag } from "src/contexts/FeatureFlags/FeatureFlagContext";
import { FeatureFlagType } from "src/generated/graphqlTypes";
import { useRouteParams } from "src/hooks/useRouteParams";
import {
  PlanPackagePageV2MetadataQuery,
  usePlanPackagePageV2MetadataQuery,
} from "src/routes/libraries/plan-package-v2/PlanPackagePageV2.generated";
import { CheckoutTab } from "src/routes/libraries/plan-package-v2/components/CheckoutTab";
import { ElevationsTab } from "src/routes/libraries/plan-package-v2/components/ElevationsTab";
import { LocationsTab } from "src/routes/libraries/plan-package-v2/components/LocationsTab";
import { OptionsTab } from "src/routes/libraries/plan-package-v2/components/OptionsTab";
import { OverviewTab } from "src/routes/libraries/plan-package-v2/components/OverviewTab";
import { PlanPackageHeaderActions } from "src/routes/libraries/plan-package-v2/components/PlanPackageHeaderActions";
import { ProgramDataTab } from "src/routes/libraries/plan-package-v2/components/ProgramDataTab";
import { TakeoffsTab } from "src/routes/libraries/plan-package-v2/components/TakeoffsTab";
import { DocumentsPage } from "src/routes/projects/documents/DocumentsPage";
import { PlanPackageDetailsParams } from "src/routes/routesDef";
import * as librariesPaths from "src/routing/domains/librariesPaths";
import { queryResult } from "src/utils";

export type PlanPackageTabProps = { planPackageId: string; versionId: string };

export function PlanPackagePageV2() {
  const isPlanPackagesAiEnabled = useFeatureFlag(FeatureFlagType.PlanPackagesAi);
  const { planPackageId, planPackageVersionId } = useRouteParams<PlanPackageDetailsParams>();
  const query = usePlanPackagePageV2MetadataQuery({
    variables: { id: planPackageId, versionId: planPackageVersionId },
  });
  return queryResult(query, ({ planPackage }) => (
    <DataView planPackage={planPackage} isPlanPackagesAiEnabled={isPlanPackagesAiEnabled} />
  ));
}

type DataViewProps = { planPackage: PlanPackagePageV2MetadataQuery["planPackage"]; isPlanPackagesAiEnabled: boolean };

function DataView({ planPackage, isPlanPackagesAiEnabled }: DataViewProps) {
  const { planPackageId, planPackageVersionId } = useRouteParams<PlanPackageDetailsParams>();
  const isPpV2TakeoffsEnabled = useFeatureFlag(FeatureFlagType.PpV2Takeoffs);
  const aiProposal = isPlanPackagesAiEnabled ? planPackage.aiProposal : undefined;
  const tabs: RouteTabWithContent[] = useMemo(() => {
    const params = { planPackageId, planPackageVersionId };
    return [
      {
        name: "Overview",
        href: librariesPaths.planPackageOverviewRoute.url(params),
        path: [librariesPaths.planPackageRoute.path, librariesPaths.planPackageOverviewRoute.path],
        render: () => <OverviewTab planPackageId={planPackageId} versionId={planPackageVersionId} />,
      },
      {
        name: "Elevations",
        href: librariesPaths.planPackageElevationsRoute.url(params),
        path: librariesPaths.planPackageElevationsRoute.path,
        aiMode: !!aiProposal?.hasElevationProposals,
        render: () => <ElevationsTab planPackageId={planPackageId} versionId={planPackageVersionId} />,
      },
      {
        name: "Program Data",
        href: librariesPaths.planPackageProgramDataRoute.url(params),
        path: librariesPaths.planPackageProgramDataRoute.path,
        aiMode: !!aiProposal?.hasProgramDataProposals,
        render: () => <ProgramDataTab planPackageId={planPackageId} versionId={planPackageVersionId} />,
      },
      {
        name: "Locations",
        href: librariesPaths.planPackageLocationsRoute.url(params),
        path: librariesPaths.planPackageLocationsRoute.path,
        aiMode: !!aiProposal?.hasLocationProposals,
        render: () => <LocationsTab planPackageId={planPackageId} versionId={planPackageVersionId} />,
      },
      {
        name: "Options",
        href: librariesPaths.planPackageOptionsRoute.url(params),
        path: librariesPaths.planPackageOptionsRoute.path,
        aiMode: !!aiProposal?.hasOptionProposals,
        render: () => <OptionsTab planPackageId={planPackageId} versionId={planPackageVersionId} />,
      },
      isPpV2TakeoffsEnabled
        ? {
            name: "Takeoffs",
            href: librariesPaths.planPackageTakeoffsRoute.url(params),
            path: librariesPaths.planPackageTakeoffsRoute.path,
            render: () => <TakeoffsTab planPackageId={planPackageId} versionId={planPackageVersionId} />,
          }
        : {
            name: "Takeoffs",
            // Links out to the legacy takeoffs page until PP_V2_TAKEOFFS is released. With the flag off, or before
            // flags load, a V2 takeoffs URL matches no tab, so it shows the Overview tab without redirecting.
            href: librariesPaths.planTakeoffsRoute.url(params),
            path: librariesPaths.planTakeoffsRoute.path,
            render: () => <></>,
          },
      {
        name: "Checkout",
        href: librariesPaths.planPackageCheckoutRoute.url(params),
        path: librariesPaths.planPackageCheckoutRoute.path,
        render: () => <CheckoutTab planPackageId={planPackageId} versionId={planPackageVersionId} />,
      },
      {
        name: "Documents",
        href: librariesPaths.planPackageDocumentsRoute.url(params),
        path: librariesPaths.planPackageDocumentsRoute.path,
        // Shared DocumentsPage; GridTableLayout + hideHeader keep Plan Package V2 chrome (no duplicate header / legacy TableActions).
        render: () => (
          <DocumentsPage readyPlanId={planPackageId} versionId={planPackageVersionId} hideHeader useGridTableLayout />
        ),
      },
    ];
  }, [planPackageId, planPackageVersionId, aiProposal, isPpV2TakeoffsEnabled]);

  return (
    <PageHeaderLayout
      pageHeader={{
        title: planPackage.name,
        breadcrumbs: { breadcrumbs: { label: "Plan Library", href: createPlanPackagesUrl() } },
        tabs: { tabs },
        rightSlot: <PlanPackageHeaderActions planPackage={planPackage} />,
      }}
    >
      <TabContent tabs={tabs} />
    </PageHeaderLayout>
  );
}
